import React, { createContext, useContext, useState, useEffect } from 'react';
import { useAuth } from './AuthContext';
import { getUserSubscription } from '../services/stripeService';

// Free tier limitations
export const FREE_LIMITS = {
  maxGoals: 3,
  maxTalents: 3,
} as const;

interface SubscriptionContextType {
  isPremium: boolean;
  isLoading: boolean;
  subscriptionPlan: 'free' | 'premium' | null;
  upgradeUrl: string | null;
  cancelAtPeriodEnd: boolean;
  subscriptionPeriodEndAt: string | null;
}

const SubscriptionContext = createContext<SubscriptionContextType | undefined>(undefined);

export const useSubscription = () => {
  const context = useContext(SubscriptionContext);
  if (context === undefined) {
    throw new Error('useSubscription must be used within a SubscriptionProvider');
  }
  return context;
};

export const SubscriptionProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  const [isPremium, setIsPremium] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [subscriptionPlan, setSubscriptionPlan] = useState<'free' | 'premium' | null>(null);
  const [upgradeUrl, setUpgradeUrl] = useState<string | null>(null);
  const [cancelAtPeriodEnd, setCancelAtPeriodEnd] = useState(false);
  const [subscriptionPeriodEndAt, setSubscriptionPeriodEndAt] = useState<string | null>(null);

  useEffect(() => {
    const checkSubscriptionStatus = async () => {
      if (!user) {
        setIsPremium(false);
        setSubscriptionPlan(null);
        setIsLoading(false);
        return;
      }

      try {
        // Add timeout to subscription check to prevent hanging
        const subscriptionPromise = getUserSubscription(user.id);
        const timeoutPromise = new Promise((_, reject) => 
          setTimeout(() => reject(new Error('Subscription check timeout')), 1000)
        );

        const data = await Promise.race([subscriptionPromise, timeoutPromise]) as any;

        // Determine premium status based on subscription_tier and status
        const isPremiumActive = data?.subscription_tier === 'premium' && 
                               (data?.subscription_status === 'active' || data?.subscription_status === 'trialing');
        
        setIsPremium(isPremiumActive);
        setSubscriptionPlan(isPremiumActive ? 'premium' : 'free');
        
        // Set cancellation information
        const cancelAtPeriodEndValue = data?.cancel_at_period_end || false;
        const periodEndDate = data?.subscription_period_end_at || null;
        
        setCancelAtPeriodEnd(cancelAtPeriodEndValue);
        setSubscriptionPeriodEndAt(periodEndDate);
        setUpgradeUrl('/pricing');
        
      } catch (error) {
        console.error('Error checking subscription status:', error);
        // Set defaults on error to avoid blocking UI
        setIsPremium(false);
        setSubscriptionPlan('free');
        setCancelAtPeriodEnd(false);
        setSubscriptionPeriodEndAt(null);
        setUpgradeUrl('/pricing');
      } finally {
        setIsLoading(false);
      }
    };

    checkSubscriptionStatus();
  }, [user]);

  return (
    <SubscriptionContext.Provider 
      value={{ 
        isPremium, 
        isLoading, 
        subscriptionPlan, 
        upgradeUrl,
        cancelAtPeriodEnd,
        subscriptionPeriodEndAt
      }}
    >
      {children}
    </SubscriptionContext.Provider>
  );
}; 